import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useBoardMembers } from '../hooks/useBoardMembers';

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export default function BoardMembers() {
  const { publicMembers, isLoading, syncError, storageMode } = useBoardMembers();

  return (
    <div className="page-shell board-page space-y-8">
      <section className="board-hero">
        <div className="board-hero-copy">
          <span className="eyebrow">Leadership</span>
          <p className="about-kicker">Board members</p>
          <h1>The people helping shape and steward Victoria&apos;s Place.</h1>
          <p className="board-hero-lead">
            Our board brings lived experience, community roots, and practical support to the work.
            Each profile helps families, partners, and supporters see the people carrying the mission
            forward.
          </p>
          <div className="board-hero-actions">
            <Link to="/about-us" className="button-secondary tap-target">
              Back to about us
            </Link>
            <Link to="/sign-in?redirect=%2Fportal%2Fboard" className="button-primary tap-target">
              Board member sign in
            </Link>
          </div>
        </div>

        <div className="board-hero-note">
          <p className="info-kicker">Board access</p>
          <h2>Board members can now maintain their own profile.</h2>
          <p>
            Signed-in board members can add or update their bio and photo, while admins can edit the
            full roster and remove entries when needed.
          </p>
        </div>
      </section>

      {storageMode === 'local' && syncError ? (
        <div className="auth-runtime-note board-runtime-note">
          <p>{syncError}</p>
          <p>Showing the current seeded board roster in local mode.</p>
        </div>
      ) : null}

      <section className="board-directory">
        <div className="board-directory-heading">
          <div>
            <p className="home-section-label">Current board</p>
            <h2>Meet the board</h2>
          </div>
          <p>
            This page is designed to grow with the organization. New members can be added as the
            roster evolves, and existing biographies can be refreshed through the portal.
          </p>
        </div>

        {isLoading ? (
          <div className="auth-runtime-note">
            <p>Loading board profiles...</p>
          </div>
        ) : publicMembers.length ? (
          <div className="board-member-grid">
            {publicMembers.map((member, index) => (
              <motion.article
                key={member.id}
                className="board-member-card"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35, delay: index * 0.04 }}
              >
                <div className="board-member-card-photo-shell">
                  {member.photo_url ? (
                    <img
                      className="board-member-card-photo"
                      src={member.photo_url}
                      alt={member.photo_alt || `${member.name} portrait`}
                    />
                  ) : (
                    <div className="board-member-card-placeholder" aria-hidden="true">
                      <span>{getInitials(member.name)}</span>
                    </div>
                  )}
                </div>

                <div className="board-member-card-copy">
                  <p className="board-member-card-kicker">Board member</p>
                  <h3>{member.name}</h3>
                  <p className="board-member-card-title">{member.board_title}</p>
                  {member.bio
                    .split('\n\n')
                    .filter(Boolean)
                    .map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                </div>
              </motion.article>
            ))}
          </div>
        ) : (
          <div className="auth-runtime-note">
            <p>No public board member profiles have been published yet.</p>
          </div>
        )}
      </section>
    </div>
  );
}
